import { Link } from "react-router-dom";
import '../styles/EventsStyles/Events.css';
import EventsLayout from "../components/EventsSection/EventsLayout";
import IntroVideo from "../components/EventsSection/IntroVideo";
import WebinarCard from "../components/EventsSection/WebinarCard";
import StorageErrorNotice from "../components/EventsSection/StorageErrorNotice";
import { ROLES } from "../webinars/config";
import { webinarRepository } from "../webinars/repository";
import { useRepositoryData } from "../webinars/useRepositoryData";
import { isPastWebinar, isPublicWebinar, upcomingWebinars } from "../webinars/availability";
import { formatLongDate } from "../webinars/format";
import { presenterPhotoUrl } from "../webinars/photos";

const registrationSteps = [
    "Choose a webinar below.",
    "Fill in the short form.",
    "We email your confirmation, then your joining link before the session.",
];

const Events = () => {
    const { data, loading, error } = useRepositoryData(async () => {
        const [webinars, counts] = await Promise.all([
            webinarRepository.listPublicWebinars(),
            webinarRepository.countActiveRegistrations(),
        ]);
        return { webinars, counts };
    }, "events");

    // Drafts never reach this page; cancelled events stay off the listing too
    const listed = (data?.webinars ?? []).filter(w => isPublicWebinar(w) && w.status !== "cancelled");
    const upcoming = upcomingWebinars(listed);
    const past = listed
        .filter(w => isPastWebinar(w))
        .sort((a, b) => Date.parse(b.startAt) - Date.parse(a.startAt));

    return (
        <EventsLayout>
            {/* ── HERO ── */}
            <section className="ev-hero">
                <div className="km-container ev-hero-grid">
                    <div className="km-enter">
                        <p className="km-eyebrow">Events &amp; regulatory learning</p>
                        <h1>Webinars on medical devices and reagents.</h1>
                        <p className="ev-hero-lead">
                            Live online sessions on medical devices, reagents and regulatory compliance, led by Tebogo Kole
                            and invited industry experts. For anyone working in, or entering, South Africa's medical device
                            industry. Open to everyone. No licence needed.
                        </p>
                        <div className="ev-hero-actions">
                            <Link className="km-btn km-btn--primary" to={{ hash: "#upcoming" }}>See upcoming webinars</Link>
                            <Link className="km-btn km-btn--outline" to={{ hash: "#how" }}>How registration works</Link>
                        </div>
                    </div>
                    <IntroVideo />
                </div>
            </section>

            {/* ── WHAT / WHO / HOW ── */}
            <section className="km-container km-cardRow ev-info" id="how">
                <div className="km-card" data-reveal style={{ "--reveal-order": 0 } as React.CSSProperties}>
                    <h2>What the webinars cover</h2>
                    <p>
                        Medical devices and reagents in South Africa: how the market works, what regulators expect,
                        and practical steps for running a compliant business.
                    </p>
                </div>
                <div className="km-card" data-reveal style={{ "--reveal-order": 1 } as React.CSSProperties}>
                    <h2>Who should attend</h2>
                    <ul className="ev-roles">
                        {ROLES.filter(role => role !== "Other").map(role => <li key={role}>{role}</li>)}
                    </ul>
                </div>
                <div className="km-card" data-reveal style={{ "--reveal-order": 2 } as React.CSSProperties}>
                    <h2>How registration works</h2>
                    <ol className="ev-steps">
                        {registrationSteps.map((step, index) => (
                            <li key={step}><span aria-hidden="true">{index + 1}</span>{step}</li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* ── UPCOMING WEBINARS ── */}
            <section className="km-container ev-upcoming" id="upcoming">
                <div data-reveal>
                    <h2 className="km-title">Upcoming webinars</h2>
                    <p className="ev-upcoming-note">Times are South African Standard Time (SAST).</p>
                </div>

                {loading && !data ? (
                    <p className="ev-empty" role="status">Loading webinars…</p>
                ) : error ? (
                    <StorageErrorNotice error={error} what="the webinars" />
                ) : upcoming.length > 0 ? (
                    <div className="ev-grid">
                        {upcoming.map((webinar, index) => (
                            <WebinarCard
                                key={webinar.id}
                                webinar={webinar}
                                activeCount={data?.counts[webinar.id] ?? 0}
                                presenterPhotoUrl={presenterPhotoUrl(webinar.presenterPhoto)}
                                revealOrder={index}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="ev-empty">
                        No webinars are scheduled right now. Check back soon, or{" "}
                        <Link to="/query">ask us about upcoming sessions</Link>.
                    </p>
                )}
            </section>

            {/* ── PAST EVENTS ── */}
            {past.length > 0 && (
                <section className="km-container ev-past" id="past">
                    <h2 className="km-title" data-reveal>Past events</h2>
                    <ul className="ev-past-list">
                        {past.map((webinar, index) => (
                            <li key={webinar.id} className="km-card ev-past-item" data-reveal style={{ "--reveal-order": index } as React.CSSProperties}>
                                <div>
                                    <p className="ev-past-date">
                                        {formatLongDate(webinar.startAt)}
                                        {webinar.isDemo && <span className="ev-chip ev-chip--demo">Demo</span>}
                                    </p>
                                    <h3>{webinar.title}</h3>
                                    {webinar.presenter && <p className="ev-past-presenter">Presented by {webinar.presenter}</p>}
                                </div>
                                <div className="ev-past-links">
                                    {webinar.recordingUrl && (
                                        <a className="km-btn km-btn--outline" href={webinar.recordingUrl} target="_blank" rel="noreferrer">Watch recording</a>
                                    )}
                                    {webinar.resourceUrl && (
                                        <a className="km-btn km-btn--outline" href={webinar.resourceUrl} target="_blank" rel="noreferrer">Resources</a>
                                    )}
                                    {!webinar.recordingUrl && !webinar.resourceUrl && (
                                        <span className="ev-past-none">No recording available</span>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </EventsLayout>
    );
};

export default Events;
